/**
 * Generate a reply: the pieces the Generate dialog shares between the staff chat composer's Generate
 * button (`Chat/ChatGenerateControl`) and the CRM desk composer. This module imports neither of them,
 * so a change on the desk cannot break the chat composer, and the reverse.
 */

export type GenerateScope = 'last' | 'thread'

/** What the dialog hands back. The staff chat button passes it to `generateCrmReply` as is. */
export type GenerateDraftRequest = {
  includeFiles: boolean
  instruction: string
  scope: GenerateScope
  /** Empty when this runs as its own AiAction on the account's reply tool. */
  toolId: string | null
}

/** A chat tool the reply can run through, besides the account's own reply tool. */
export type GenerateToolChoice = {
  label: string
  toolId: string
}

export type GenerateComment = {
  /** out = the account. Files on those messages are not sent to the model. */
  direction?: 'in' | 'out'
  files?: Array<unknown> | null
  isDraft?: boolean
  withdrawnAt?: string | null
}

/** Sent messages the Generate box can offer. Drafts and unsent messages stay out. */
export function commentsForGenerate<T extends GenerateComment>(comments: T[], scope: GenerateScope): T[] {
  const sent = comments.filter(comment => !comment.isDraft && !comment.withdrawnAt)
  return scope === 'last' ? sent.slice(-1) : sent
}

/** Files on member messages Generate would send for this scope. The account's own files stay out. */
export function generateFileCount(comments: GenerateComment[], scope: GenerateScope): number {
  return commentsForGenerate(comments, scope).reduce((count, comment) => {
    if (comment.direction === 'out') {
      return count
    }
    return count + (comment.files?.length || 0)
  }, 0)
}

/** The "include files" checkbox label for a file count. */
export function generateFilesLabel(fileCount: number): string {
  if (!Number.isFinite(fileCount) || fileCount < 1) {
    return 'No files on these messages'
  }
  const whole = Math.floor(fileCount)
  return whole === 1 ? 'Include 1 attachment' : `Include ${whole} attachments`
}

/**
 * Text as a person reads it, for comparing the box with what the rules wrote. The editor writes the
 * markdown back out with its own line breaks and escapes, so an untouched draft does not come back
 * byte for byte.
 */
function readable(text: string): string {
  return text
    .replace(/\\([\\`*_{}[\]()#+\-.!>~|$])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Writing the reply again replaces the box. That is only worth a question when the box holds something
 * the rules did not write: anything typed over the suggestion, saved or not.
 */
export function redraftNeedsConfirm(input: {
  body: string
  suggestedBody: string | null | undefined
}): boolean {
  const typed = readable(input.body)
  return typed.length > 0 && typed !== readable(input.suggestedBody || '')
}
